import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import useSharePointList from '../hooks/useSharePointList';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import { createPart, listParts } from '../services/parts';
import { listSuppliers } from '../services/suppliers';
import { listPlants } from '../services/plants';
// Coverage-hours logging used to fire from here on part create. That
// tracking has moved to the Suppliers page in the Sales dashboard, so
// the salesTracker import + event write are gone.
import { formatDateShort } from '../utils/formatters';
import { savePartFormPdf } from '../utils/partFormPdf';
import PartForm from './PartForm';

/**
 * Parts index. Shows every part across all suppliers with its supplier
 * name, plant list, and variant count. Day-to-day most Managers will add
 * parts from the supplier page (pre-filled supplier); this page is the
 * global search and catalog.
 */
export default function Parts() {
  const { canAddParts, isLiaisonManager } = usePermissions();
  // Liaison Managers only see parts used at their assigned plants (per
  // Jillian, July 2026) — same scoping as the Plants and Suppliers
  // pages. RMs and Directors keep the full catalog.
  const {
    allows: allowsPlant,
    allowsSupplier,
    loading: scopeLoading,
  } = usePlantScope();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useSharePointList(listParts);
  // Archived (deactivated) parts are hidden from the active catalog by
  // default; the "Show archived" toggle reveals them (flagged with an
  // "Archived" badge) so they stay reachable for history and restore.
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = useMemo(
    () => (data || []).filter((p) => p.archived).length,
    [data],
  );
  const visibleRows = useMemo(() => {
    let rows = data || [];
    // Hide archived parts unless the user opts to see them.
    if (!showArchived) rows = rows.filter((p) => !p.archived);
    // Liaison Managers additionally only see parts at their assigned plants.
    if (isLiaisonManager) {
      rows = rows.filter(
        (p) =>
          (p.plantVehicles || []).some((pv) => allowsPlant(pv.plantCode)) &&
          // Sep 2026: and only parts from suppliers the LM is responsible
          // for (see utils/supplierScope.js).
          allowsSupplier(p.SupplierCode),
      );
    }
    // Each part can have several part types (variants), each with its own
    // Part # and Part Description — the list shows every one, aligned so a
    // description sits on the same line as its part number. Build one
    // ordered list of {num, desc} pairs from the variants (falling back to
    // the legacy record-level PartNumber / Description if a part has no
    // variants yet). Both the Part #/s and Part Description columns render
    // from this same list so the rows line up; the joined strings feed the
    // table's search + sort.
    return rows.map((p) => {
      let entries = (p.variants || [])
        .map((v) => ({
          num: (v.partNumber || '').trim(),
          desc: (v.description || '').trim(),
        }))
        .filter((e) => e.num || e.desc);
      if (entries.length === 0) {
        const num = (p.PartNumber || '').trim();
        const desc = (p.Description || '').trim();
        if (num || desc) entries = [{ num, desc }];
      }
      return {
        ...p,
        _partRows: entries,
        partNumbers: entries.map((e) => e.num).filter(Boolean).join(', '),
        partDescriptions: entries.map((e) => e.desc).filter(Boolean).join(', '),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showArchived, isLiaisonManager, allowsPlant, allowsSupplier, scopeLoading]);

  // Resolve supplier codes → names for display. Cheap; Suppliers list is small.
  const [supplierByCode, setSupplierByCode] = useState({});
  // Full supplier records, handed to PartForm so its Supplier field can
  // render as a dropdown.
  const [suppliers, setSuppliers] = useState([]);
  // Plants are needed so the New-part form can show the plant+vehicle picker.
  const [plants, setPlants] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [supRows, plantRows] = await Promise.all([
          listSuppliers(),
          listPlants(),
        ]);
        if (cancelled) return;
        const map = {};
        for (const s of supRows) {
          if (s.SupplierCode) map[s.SupplierCode] = s.Title || s.SupplierCode;
        }
        setSupplierByCode(map);
        setSuppliers(supRows || []);
        setPlants(plantRows || []);
      } catch {
        // Non-fatal — falls back to showing codes / empty plant list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  // Parts grouped by plant (per Jillian, Oct 2026): one collapsible section
  // per plant, alphabetical by plant code. A part at several plants is
  // listed under each of them (showing just that plant's vehicles there);
  // parts with no plant land in a "No Plant" section at the bottom.
  const NO_PLANT = '~NO_PLANT';
  const plantTitleByCode = useMemo(() => {
    const m = new Map();
    for (const pl of plants || []) {
      if (pl.PlantCode) m.set(String(pl.PlantCode).toUpperCase(), pl.Title || '');
    }
    return m;
  }, [plants]);
  const rowsByPlant = useMemo(() => {
    const out = [];
    for (const p of visibleRows) {
      const pvs = p.plantVehicles || [];
      const codes = [
        ...new Set(
          pvs.map((pv) => String(pv.plantCode || '').toUpperCase().trim()).filter(Boolean),
        ),
      ].filter((code) => !isLiaisonManager || allowsPlant(code));
      if (codes.length === 0) {
        out.push({ ...p, id: `${p.id}|${NO_PLANT}`, _plantGroup: NO_PLANT });
        continue;
      }
      for (const code of codes) {
        out.push({
          ...p,
          id: `${p.id}|${code}`,
          _plantGroup: code,
          plantVehicles: pvs.filter(
            (pv) => String(pv.plantCode || '').toUpperCase().trim() === code,
          ),
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRows, isLiaisonManager, allowsPlant]);
  const plantGroupOrder = useMemo(
    () =>
      [...new Set(rowsByPlant.map((r) => r._plantGroup))].sort((a, b) => {
        if (a === NO_PLANT) return 1;
        if (b === NO_PLANT) return -1;
        return a.localeCompare(b);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsByPlant],
  );
  const groupByPlant = useMemo(
    () => ({
      getKey: (r) => r._plantGroup,
      getLabel: (code) => {
        if (code === NO_PLANT) return 'No Plant';
        const title = plantTitleByCode.get(code);
        return title ? `${code} — ${title}` : code;
      },
      order: plantGroupOrder,
      collapsible: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plantGroupOrder, plantTitleByCode],
  );

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dashboard quick actions link here with ?new=1 to open the New Part
  // form straight away (Director's "Add Part" and the LM's "Add a New
  // Part"). The param is cleared after opening so closing the modal or
  // refreshing doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1' && canAddParts) {
      setCreating(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNew = () => setCreating(true);

  const onSave = useCallback(
    async (model) => {
      setSaving(true);
      try {
        await createPart(model);
        setCreating(false);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh],
  );

  const columns = useMemo(
    () => [
      {
        key: 'SupplierCode',
        label: 'Supplier',
        // Narrowed so the Part Description column below can be wider; long
        // supplier names just wrap onto a second line.
        width: '15%',
        sortValue: (r) =>
          r.SupplierCode ? supplierByCode[r.SupplierCode] || r.SupplierCode : '',
        render: (r) =>
          r.SupplierCode ? supplierByCode[r.SupplierCode] || r.SupplierCode : '—',
      },
      // Plant/s and Vehicles are split into two columns so the table
      // matches the Supplier detail page layout. Each plant-vehicle
      // pair is stacked on its own row inside both cells, so the
      // plant on line N aligns horizontally with the vehicle on line
      // N. Sorted by pair count — the array itself isn't a useful
      // sort key.
      {
        key: 'plantVehicles',
        label: 'Plant/s',
        filter: false,
        sortValue: (r) => (r.plantVehicles || []).length,
        render: (r) => {
          const rows = r.plantVehicles || [];
          if (rows.length === 0) return <span className="muted">—</span>;
          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              {rows.map((pv, i) => (
                <span
                  key={i}
                  className="badge plant"
                  style={{ width: 'fit-content' }}
                >
                  {pv.plantCode}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'vehicles',
        label: 'Vehicles',
        filter: false,
        // Trimmed from the phone view — the mobile search results show only
        // Supplier, Plant/s, and Part name; the rest live on the part detail
        // page.
        mobileHidden: true,
        sortValue: (r) =>
          (r.plantVehicles || []).filter((pv) => pv.vehicle).length,
        render: (r) => {
          const rows = r.plantVehicles || [];
          if (rows.length === 0) return <span className="muted">—</span>;
          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              {rows.map((pv, i) =>
                pv.vehicle ? (
                  <span
                    key={i}
                    className="badge vehicle"
                    style={{ width: 'fit-content' }}
                  >
                    {pv.vehicle}
                  </span>
                ) : (
                  <span
                    key={i}
                    className="muted"
                    style={{ fontSize: 12 }}
                  >
                    —
                  </span>
                ),
              )}
            </div>
          );
        },
      },
      {
        key: 'Title',
        label: 'Part name',
        width: '16%',
        // When archived parts are shown, flag them inline so they're easy
        // to tell apart from active ones in the same list.
        render: (r) => (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            {r.Title || '—'}
            {r.archived && (
              <span
                className="badge"
                style={{
                  background: 'var(--brand-surface-muted, #f1f5f9)',
                  color: 'var(--brand-muted, #64748b)',
                  border: '1px solid var(--brand-border, #e5e7eb)',
                  fontSize: 11,
                }}
              >
                Archived
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'partNumbers',
        label: 'Part #/s',
        mobileHidden: true,
        // Sort/search read the joined string stashed on each row above.
        sortValue: (r) => r.partNumbers || '',
        render: (r) => {
          const entries = r._partRows || [];
          if (entries.length === 0) return <span className="muted">—</span>;
          if (entries.length === 1) {
            return entries[0].num || <span className="muted">—</span>;
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {entries.map((e, i) => (
                <span key={i}>{e.num || '—'}</span>
              ))}
            </div>
          );
        },
      },
      {
        // Replaces the old "Variants" count column — shows each part
        // type's description on the same line as its Part # above.
        key: 'partDescriptions',
        label: 'Part Description',
        // Widened (using the space reclaimed from the Supplier column) so
        // descriptions have room before they wrap.
        width: '22%',
        mobileHidden: true,
        sortValue: (r) => r.partDescriptions || '',
        render: (r) => {
          const entries = r._partRows || [];
          if (entries.length === 0) return <span className="muted">—</span>;
          if (entries.length === 1) {
            return entries[0].desc || <span className="muted">—</span>;
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {entries.map((e, i) => (
                <span key={i}>{e.desc || '—'}</span>
              ))}
            </div>
          );
        },
      },
      // Last Updated — SharePoint's Modified system column. Sort key is
      // the parsed timestamp so newest/oldest ordering is chronological.
      // Falls back to Created for rows that haven't been edited since.
      {
        key: 'Modified',
        label: 'Last Updated',
        mobileHidden: true,
        sortValue: (r) => new Date(r.Modified || r.Created || 0).getTime(),
        render: (r) => {
          const v = r.Modified || r.Created;
          return v ? formatDateShort(v) : <span className="muted">—</span>;
        },
      },
    ],
    [supplierByCode],
  );

  const onRowClick = (row) => {
    if (row.PartCode) navigate(`/parts/${encodeURIComponent(row.PartCode)}`);
  };

  return (
    <>
      <div className="page-header">
        <h1>Parts</h1>
        <div className="actions">
          {/* Show/hide archived (deactivated) parts. Only offered when
              there's at least one archived part, so it stays out of the way
              otherwise. */}
          {archivedCount > 0 && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14,
                color: 'var(--brand-muted)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived ({archivedCount})
            </label>
          )}
          {/* "Part Form" downloads a blank, printable PDF of the New Part
              form fields. Visible to everyone — Managers / Liaisons often
              want a paper copy to fill in during a plant walk and hand
              back to a Director. The PDF itself is generated in the
              browser via utils/partFormPdf.js, no server round trip. */}
          <button
            className="btn btn-outline"
            onClick={() => {
              savePartFormPdf().catch((err) => {
                // eslint-disable-next-line no-console
                console.error('[parts] failed to build part form PDF', err);
                alert('Sorry — couldn\u2019t build the Part Form PDF. Please try again.');
              });
            }}
          >
            Printable Part Form
          </button>
          {canAddParts && (
            <button className="btn btn-primary" onClick={onNew}>
              + New Part
            </button>
          )}
        </div>
      </div>

      {(loading || (isLiaisonManager && scopeLoading)) && <Loading />}
      {error && (
        <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
          <div className="card-title">Couldn't Load Parts</div>
          <p>{error.message}</p>
          <button className="btn btn-outline btn-sm" onClick={refresh}>
            Retry
          </button>
        </div>
      )}
      {!loading && !error && !(isLiaisonManager && scopeLoading) && (
        <DataTable
          rows={rowsByPlant}
          columns={columns}
          groupBy={groupByPlant}
          onRowClick={onRowClick}
          mobileSearchOnly
          searchLabel="Search Parts"
          emptyMessage={
            isLiaisonManager
              ? 'No parts at your assigned plants yet.'
              : 'No parts yet. Click “+ New Part” to add one.'
          }
        />
      )}

      <Modal
        open={creating}
        onClose={() => (saving ? null : setCreating(false))}
        title="New Part"
      >
        {creating && (
          <PartForm
            initial={{}}
            plants={plants}
            suppliers={suppliers}
            // Scope the Plant dropdown to the plants this user is assigned
            // to (Liaison Managers see only their own; Directors/RMs see
            // all). allowsPlant returns true for unscoped roles.
            allowsPlant={allowsPlant}
            // The current Parts dataset is already in memory — hand the
            // codes to the form so it can both suggest the next-available
            // code for a supplier and block duplicates on save.
            existingPartCodes={(data || [])
              .map((p) => p.PartCode)
              .filter(Boolean)}
            saving={saving}
            onSave={onSave}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </>
  );
}

